/**
 * Canonical URLs must always point at the production hotpads.com origin,
 * regardless of where the page is rendered (preview deploys, staging,
 * alternate hosts). Basing canonicals on request hosts or env-injected preview
 * URLs would leak non-production URLs into indexed canonicals.
 */
export const CANONICAL_ORIGIN = 'https://hotpads.com';

/**
 * Default site description used by the root metadata, the homepage metadata,
 * and the PWA manifest. The i18n equivalent lives at `seo.defaultDescription`
 * in the messages catalogs; this constant is the English source of truth for
 * non-localized surfaces.
 */
export const DEFAULT_SITE_DESCRIPTION =
  'Browse the largest and most trusted apartment and home rental site with our verified listings, fraud protection technology and real-time property updates.';

/**
 * Route params arrive percent-decoded while API hrefs and sitemaps carry the
 * encoded form (e.g. `%28230%29` vs `(230)`); normalize every segment to the
 * encoded form so canonical URLs never split into encoding-variant clusters.
 * Decode-then-encode makes this idempotent for both input kinds.
 */
function encodeCanonicalPath(path: string): string {
  return path
    .split('/')
    .map((segment) => {
      let decoded = segment;
      try {
        decoded = decodeURIComponent(segment);
      } catch {
        // Not valid percent-encoding: encode the raw segment as-is.
      }
      return encodeURIComponent(decoded).replace(
        /[!'()*]/g,
        (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`,
      );
    })
    .join('/');
}

/**
 * Build an absolute canonical URL on the hotpads.com origin.
 *
 * - Accepts an absolute pathname (leading slash optional) — never a full URL.
 * - Strips query/hash by default; pass `preserveQuery: true` to keep the query string.
 */
export function canonicalUrl(pathname: string, options: { preserveQuery?: boolean } = {}): string {
  const { preserveQuery = false } = options;
  const [pathAndQuery] = pathname.split('#');
  const [rawPath, rawQuery] = pathAndQuery.split('?');
  const path = encodeCanonicalPath(rawPath.startsWith('/') ? rawPath : `/${rawPath}`);
  const query = preserveQuery && rawQuery ? `?${rawQuery}` : '';
  return `${CANONICAL_ORIGIN}${path}${query}`;
}

/** Shared server-rendered image for non-listing social previews. */
export const DEFAULT_SOCIAL_PREVIEW_IMAGE_URL = canonicalUrl('/icon.png');
export const DEFAULT_SOCIAL_PREVIEW_IMAGE_ALT = 'HotPads logo';

/**
 * Build hreflang alternate URLs for a given unprefixed pathname.
 * Spanish URLs are intentionally omitted while `/es` is a temporary stub.
 * Always includes `x-default` pointing at the English URL.
 */
export function hreflangAlternates(pathname: string): Record<'en' | 'x-default', string> {
  const enUrl = canonicalUrl(pathname);
  return {
    en: enUrl,
    'x-default': enUrl,
  };
}
